import { LineItem } from 'interfaces/line-item'
import { LineItemDisplay } from './line-item-manager'

/**
 * Service for managing line item persistence in localStorage
 * Handles loading and saving line items and their backend-ready counterparts
 */
export class LineItemStorage {
  private static readonly DISPLAY_ITEMS_KEY = 'subscription-builder-line-items'

  private static readonly SAVED_ITEMS_KEY =
    'subscription-builder-saved-line-items'

  private static readonly MODAL_KEY_PREFIX = 'modal-'

  /**
   * Load line items from localStorage
   * Also clears obsolete modal-* keys from previous sessions
   */
  static loadLineItems(): {
    displayItems: LineItemDisplay[]
    savedItems: Partial<LineItem>[]
  } {
    try {
      // Clean up old modal keys
      const keysToRemove: string[] = []
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.MODAL_KEY_PREFIX)) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key))

      // Load display items
      const savedItems = localStorage.getItem(this.DISPLAY_ITEMS_KEY)
      const displayItems = savedItems ? JSON.parse(savedItems) : []

      // Load saved items for backend
      const savedItemsStorage = localStorage.getItem(this.SAVED_ITEMS_KEY)
      const savedItemsForBackend = savedItemsStorage
        ? JSON.parse(savedItemsStorage)
        : []

      return {
        displayItems,
        savedItems: savedItemsForBackend,
      }
    } catch (error) {
      // Silent fail on localStorage errors
      return {
        displayItems: [],
        savedItems: [],
      }
    }
  }

  /**
   * Save display line items to localStorage
   */
  static saveDisplayItems(items: LineItemDisplay[]): void {
    try {
      localStorage.setItem(this.DISPLAY_ITEMS_KEY, JSON.stringify(items))
    } catch (error) {
      // Silent fail on localStorage errors
    }
  }

  /**
   * Save backend-ready line items to localStorage
   */
  static saveSavedItems(items: Partial<LineItem>[]): void {
    try {
      localStorage.setItem(this.SAVED_ITEMS_KEY, JSON.stringify(items))
    } catch (error) {
      // Silent fail on localStorage errors
    }
  }
}
